import { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useEsri } from '../../esriHooks'

import { getSelectedParcels } from '../selectors'

import { PARCEL_SERVICE_URL } from '../constants'
import MAP_DATA, { NEARBY_DISTRICT_LAYER_IDS, HAZARD_LAYER_IDS, HAZARD_MAP, NEARBY_DISTRICTS_MAP } from '../../map_data'

const useParcel = (pin) => {
  const [ready, { FeatureLayer }] = useEsri(['esri/layers/FeatureLayer'])
  const [data, setData] = useState({ isLoading: true })

  useEffect(() => {
    if (ready) {
      const layer = new FeatureLayer({
        url: PARCEL_SERVICE_URL
      })
      setData({ isLoading: true })
      layer.queryFeatures({
        where: `PIN = ${pin}`,
        outSpatialReference: { wkid: 4326 },
        returnGeometry: true,
        outFields: ['*'],
        orderByFields: ['OwnerName', 'PIN']
      }).then((result) => {
        if (result.features.length) {
          setData({ parcel: result.features[0], isLoading: false })
        }
      })
    }
  }, [ready, setData, FeatureLayer, pin])

  return [data.isLoading, data.parcel]
}

const useQuerySubLayer = (layerId, sublayerId, polygon) => {
  const [ready, { FeatureLayer, geometryEngine }] = useEsri(['esri/layers/FeatureLayer', 'esri/geometry/geometryEngine'])
  const [data, setData] = useState({ isLoading: true })

  useEffect(() => {
    if (ready && polygon) {
      const layer = new FeatureLayer({
        url: `${MAP_DATA[layerId].url}/${sublayerId}`
      })
      setData({ isLoading: true })
      layer.queryFeatures({
        geometry: polygon,
        spatialRelationship: 'intersects',
        spatialReference: { wkid: 4326 },
        outSpatialReference: { wkid: 4326 },
        returnGeometry: true,
        outFields: ['*']
      }).then(results => {
        const features = results.features
        if (features.length <= 1) {
          setData({ data: features.map(f => f.attributes), isLoading: false })
          return
        }
        const withAreas = features.map(f => {
          try {
            const intersection = geometryEngine.intersect(f.geometry, polygon)
            const area = intersection ? Math.abs(geometryEngine.geodesicArea(intersection, 'square-meters')) : 0
            return { attributes: f.attributes, area }
          } catch (e) {
            return { attributes: f.attributes, area: 0 }
          }
        })
        withAreas.sort((a, b) => b.area - a.area)
        setData({ data: withAreas.map(f => f.attributes), isLoading: false })
      })
    }
  }, [ready, FeatureLayer, geometryEngine, layerId, sublayerId, polygon])

  return [data.isLoading, data.data]
}

const useElementarySchool = (polygon) => {
  const [isLoading, districts] = useQuerySubLayer(NEARBY_DISTRICTS_MAP, NEARBY_DISTRICT_LAYER_IDS.ELEMENTARY_SCHOOLS, polygon)

  const school = !isLoading && districts.length ? districts[0].school : 'None'

  return [isLoading, school]
}

const useMiddleSchool = (polygon) => {
  const [isLoading, districts] = useQuerySubLayer(NEARBY_DISTRICTS_MAP, NEARBY_DISTRICT_LAYER_IDS.MIDDLE_SCHOOLS, polygon)

  const school = !isLoading && districts.length ? districts[0].school : 'None'

  return [isLoading, school]
}

const useHighSchool = (polygon) => {
  const [isLoading, districts] = useQuerySubLayer(NEARBY_DISTRICTS_MAP, NEARBY_DISTRICT_LAYER_IDS.HIGH_SCHOOLS, polygon)

  const school = !isLoading && districts.length ? districts[0].school : 'None'

  return [isLoading, school]
}

const useEvacuationZone = (polygon) => {
  const [isLoading, zones] = useQuerySubLayer(HAZARD_MAP, HAZARD_LAYER_IDS.EVACUATION_ZONES, polygon)

  const zone = !isLoading && zones.length ? zones[0].Zone : 'None'

  return [isLoading, zone]
}

const useFloodZone = (polygon) => {
  const [isLoading, zones] = useQuerySubLayer(HAZARD_MAP, HAZARD_LAYER_IDS.PROPOSED_FLOOD_ZONES, polygon)

  const zone = !isLoading && zones.some(z => z.FLD_ZONE !== 'X') ? 'YES' : 'NO'
  
  return [isLoading, zone]
}

const useMunicipality = (polygon) => {
  const [isLoading, municipalities] = useQuerySubLayer(NEARBY_DISTRICTS_MAP, NEARBY_DISTRICT_LAYER_IDS.MUNICIPALITIES, polygon)

  const municipality = !isLoading && municipalities.length ? municipalities[0].NAME : 'None'

  return [isLoading, municipality]
}

const STORMWATER_EASEMENT_TYPES = {
  PUBLIC: "1",
  PRIVATE: "2"
}
const useStormwaterEasement = (polygon) => {
  const [isLoading, easements] = useQuerySubLayer(HAZARD_MAP, HAZARD_LAYER_IDS.STORMWATER_EASEMENT, polygon)

  const result = !isLoading && easements.length ? 'Yes' : 'None'
  // let result = 'None'
  // if (!isLoading) {
  //   console.log('easements', easements)
  //   easements.forEach(easement => {
  //     switch (easement.SUBTYPE) {
  //       case STORMWATER_EASEMENT_TYPES.PRIVATE:
  //         result = result === 'None' ? 'Private' : 'Public & Private'
  //         break;
  //       case STORMWATER_EASEMENT_TYPES.PUBLIC:
  //         result = result === 'None' ? 'Public' : 'Public & Private'
  //         break;
  //       default:
  //         console.error('Unknown stormwater easement subtype: ' + easement.SUBTYPE)
  //         break;
  //     }
  //   })
  // }

  return [isLoading, result]
}



export const useDetails = (pin) => {
  const [ parcelIsLoading, parcel ] = useParcel(pin)
  const polygon = parcel && parcel.geometry
  
  const [ elementarySchoolIsLoading, elementarySchool ] = useElementarySchool(polygon)
  const [ middleSchoolIsLoading, middleSchool ] = useMiddleSchool(polygon)
  const [ highSchoolIsLoading, highSchool ] = useHighSchool(polygon)
  const [ floodZoneIsLoading, floodZone ] = useFloodZone(polygon)
  const [ evacuationZoneIsLoading, evacuationZone ] = useEvacuationZone(polygon)
  const [ municipalityIsLoading, municipality] = useMunicipality(polygon)
  const [ stormwaterEasementIsLoading, stormwaterEasement ] = useStormwaterEasement(polygon)

  return [
    { parcel, elementarySchool, middleSchool, highSchool, floodZone, evacuationZone, municipality, stormwaterEasement }, 
    { 
      parcel: parcelIsLoading, 
      elementarySchool: elementarySchoolIsLoading, 
      middleSchool: middleSchoolIsLoading,
      highSchool: highSchoolIsLoading,
      floodZone: floodZoneIsLoading,
      evacuationZone: evacuationZoneIsLoading,
      municipality: municipalityIsLoading,
      stormwaterEasement: stormwaterEasementIsLoading
    }]
}

export const useCurrentDetails = (index) => {
  const parcels = useSelector(getSelectedParcels)
  const parcel = parcels && parcels.length && parcels[index]
  const polygon = parcel && parcel.geometry
  
  const [ elementarySchoolIsLoading, elementarySchool ] = useElementarySchool(polygon)
  const [ middleSchoolIsLoading, middleSchool ] = useMiddleSchool(polygon)
  const [ highSchoolIsLoading, highSchool ] = useHighSchool(polygon)
  const [ floodZoneIsLoading, floodZone ] = useFloodZone(polygon)
  const [ evacuationZoneIsLoading, evacuationZone ] = useEvacuationZone(polygon)
  const [ municipalityIsLoading, municipality] = useMunicipality(polygon)
  const [ stormwaterEasementIsLoading, stormwaterEasement ] = useStormwaterEasement(polygon)

  return [
    { parcel: parcel && parcel.attributes, elementarySchool, middleSchool, highSchool, floodZone, evacuationZone, municipality, stormwaterEasement }, 
    { 
      elementarySchool: elementarySchoolIsLoading, 
      middleSchool: middleSchoolIsLoading,
      highSchool: highSchoolIsLoading,
      floodZone: floodZoneIsLoading,
      evacuationZone: evacuationZoneIsLoading,
      municipality: municipalityIsLoading,
      stormwaterEasement: stormwaterEasementIsLoading
    }]
}